import { useState } from 'react';
import { colorPalette } from '@sharefiledev/antd-config';
import { App, Button, Flex, Modal } from 'antd';
import { useHistory } from 'react-router-dom';
import { useAppStore } from '../../../Common/store';
import {
	DistributionMethod,
	DocumentTemplateTypes,
	DocumentTransactionStatus,
	IdentityMethod,
	SignatureRequestQuickFlowDrawerCloseTypes,
	SignatureRequestQuickFlowDrawerSteps,
	SignatureRequestStatus,
	SignatureRequestTypes,
} from '../../../constants';
import { DocumentTransactionUpdateParams } from '../../../data/documentTransaction';
import { ESignatureClient } from '../../../data/eSignatureClient';
import { Recipient, WorkflowTypes } from '../../../data/rsTypes';
import { startDocumentPath } from '../../../routes';
import {
	BulkSend,
	SendSignatureRequestFooterAnalyticsIds,
	SignatureRequestSentTrackEvent,
} from '../../../Tracking/events';
import { getSignatureRequestFlowValue } from '../../../Tracking/helper';
import { sendPendoTrackingEvent } from '../../../Tracking/SignatureEvent';
import { t } from '../../../util';
import { getHistoryObject } from '../../../util/historyUtils';
import { emailValidator } from '../../../util/validator';

const getNextButtonText = (currentStep: SignatureRequestQuickFlowDrawerSteps) => {
	let buttonText: string;
	if (currentStep === SignatureRequestQuickFlowDrawerSteps.ReviewAndSend) {
		buttonText = t('sendSignatureRequestQuickFlow.footer.sendSignatureRequest');
	} else {
		buttonText = t('sendSignatureRequestQuickFlow.footer.reviewAndSend');
	}
	return buttonText;
};

const getPreviousButtonText = (currentStep: SignatureRequestQuickFlowDrawerSteps) => {
	let buttonText: string;
	if (currentStep === SignatureRequestQuickFlowDrawerSteps.ReviewAndSend) {
		buttonText = t('sendSignatureRequestQuickFlow.footer.backToRecipients');
	} else {
		buttonText = t('sendSignatureRequestQuickFlow.footer.startOver');
	}
	return buttonText;
};

const getAnalyticsIdForPreviousButton = (
	currentStep: SignatureRequestQuickFlowDrawerSteps
) => {
	let analyticsId: string;
	if (currentStep === SignatureRequestQuickFlowDrawerSteps.ReviewAndSend) {
		analyticsId = SendSignatureRequestFooterAnalyticsIds.PreviousStepSelectRecipients;
	} else {
		analyticsId = SendSignatureRequestFooterAnalyticsIds.StartOver;
	}
	return analyticsId;
};

export const SignatureRequestDrawerFooter = () => {
	const [modal, contextHolder] = Modal.useModal();
	const [
		roles,
		documentSignatureSettings,
		signatureRequestMetadata,
		updateSignatureRequestMetadata,
		documentTransaction,
		rsDocument,
	] = useAppStore(state => [
		state.roles,
		state.documentSignatureSettings,
		state.signatureRequestMetadata,
		state.updateSignatureRequestMetadata,
		state.document_transaction,
		state.rsDocument,
	]);

	const history = useHistory();
	const { notification } = App.useApp();
	const [isCustomizeBtnDisabled, setIsCustomizeBtnDisabled] = useState(false);

	const redirectAndShowNotification = () => {
		setTimeout(() => {
			history.push({
				...getHistoryObject(signatureRequestMetadata?.redirectPath),
				state: {
					data: {
						signatureRequestId: rsDocument?.id,
						status: SignatureRequestStatus.Success,
					},
				},
			});
		}, 500);

		updateSignatureRequestMetadata({
			drawerCloseType: SignatureRequestQuickFlowDrawerCloseTypes.Complete,
		});
	};

	const updateDocumentAndRedirect = async () => {
		updateSignatureRequestMetadata({ loadingInProgress: true });
		const documentReviewPagePayload = {
			document: {
				name: rsDocument?.name,
				expires_in: documentSignatureSettings?.expiresInDays,
				passcode: rsDocument?.passcode,
				passcode_answer: documentSignatureSettings?.passcode,
				kba: rsDocument?.kba,
				roles: mapRolesToRecipient(documentSignatureSettings.recipients, rsDocument.id),
				personalized_messages: false,
				integration_metadata: {
					origin: documentSignatureSettings.storageLocation,
				},
				distribution_method:
					documentTransaction.workflow === WorkflowTypes.SelfSign
						? DistributionMethod.Manual
						: DistributionMethod.Email,
				identity_method:
					documentTransaction.workflow === WorkflowTypes.SelfSign
						? IdentityMethod.None
						: IdentityMethod.Email,
			},
		};

		const statusPayload = createDocumentTransactionPayload(
			DocumentTransactionStatus.Live
		);

		await ESignatureClient.updateDocument(documentReviewPagePayload, rsDocument.id)
			.then(() => {
				ESignatureClient.updateDocumentTransaction(statusPayload, documentTransaction.id)
					.then(response => {
						sendPendoTrackingEvent(response, SignatureRequestSentTrackEvent, {
							docGenRequestType: SignatureRequestTypes.Quick,
							docGenDocumentId: signatureRequestMetadata.documentRID
								?.split('document-')
								?.pop(),
						});
						if (documentTransaction.workflow === WorkflowTypes.BulkSend) {
							sendPendoTrackingEvent(response, BulkSend, {
								signerCount: response.document_transaction?.signers_count,
								flow: getSignatureRequestFlowValue(SignatureRequestTypes.Quick),
							});
						}
						redirectAndShowNotification();
					})
					.catch(() => {
						notification.error({
							message: t('esign-pilet-ui:signatureRequestErrorMessage'),
						});
					});
			})
			.catch(() => {
				notification.error({ message: t('esign-pilet-ui:signatureRequestErrorMessage') });
			})
			.finally(() => {
				updateSignatureRequestMetadata({ loadingInProgress: false });
			});
	};

	const updateDocumentBulkSend = async () => {
		const documentTransactionPayload = createDocumentTransactionPayload();
		const recipients = [];
		const recipientPayload = docPayloadForUpdate(recipients, rsDocument.id, true);

		try {
			await ESignatureClient.updateDocument(recipientPayload, rsDocument.id);

			await ESignatureClient.updateDocumentTransaction(
				documentTransactionPayload,
				documentTransaction.id
			);

			updateSignatureRequestMetadata({
				currentStep: SignatureRequestQuickFlowDrawerSteps.ReviewAndSend,
			});
		} catch {
			notification.error({ message: t('genericError') });
		}
	};

	const mapRolesToRecipient = (recipients, documentId) => {
		if (documentTransaction.workflow === WorkflowTypes.BulkSend) {
			return roles;
		}
		const updatedRoles = recipients.map((recipient, idx) => {
			const matchingRole = roles.find(role => role.id === recipient.roleId);

			return {
				document_template_id: documentId,
				document_template_type: DocumentTemplateTypes.Document,
				is_sender: matchingRole.is_sender,
				message: null,
				name: matchingRole.name,
				sequence: idx,
				signer_email: recipient.email,
				signer_name: recipient.name,
			};
		});
		return updatedRoles;
	};

	const mapRecipientsToRoles = (recipients: Recipient[], documentId: string) => {
		const updatedRecipients = roles.map((role, idx) => {
			const matchingRecipient = recipients.find(
				recipient => recipient.roleId === role.id
			);

			return {
				document_template_id: documentId,
				document_template_type: DocumentTemplateTypes.Document,
				is_sender: role.is_sender,
				message: null,
				name: role.name,
				sequence: idx,
				signer_email:
					matchingRecipient && emailValidator(matchingRecipient.email).isValid
						? matchingRecipient.email
						: null,
				signer_name: matchingRecipient ? matchingRecipient.name : null,
			};
		});

		return updatedRecipients;
	};

	const docPayloadForUpdate = (
		recipients: Recipient[],
		documentId: string,
		isValidated: boolean
	) => {
		return {
			document: {
				roles: isValidated
					? mapRolesToRecipient(recipients, documentId)
					: mapRecipientsToRoles(recipients, documentId),
				signer_sequencing: rsDocument.signer_sequencing,
			},
		};
	};

	const createDocumentTransactionPayload = (
		status?: DocumentTransactionStatus
	): DocumentTransactionUpdateParams => {
		return {
			document_transaction: {
				...(status && { status }),
				workflow: documentTransaction.workflow,
				document_ids: [rsDocument.id],
			},
		};
	};

	const isValidRecipients = (recipients: Recipient[]) => {
		if (recipients.length !== roles.length) {
			notification.error({
				message: t('sendSignatureRequestQuickFlow.footer.invalidRecipientErrorMessage'),
			});
			return false;
		}
		const invalidRecipients = recipients.some(
			recipient =>
				!recipient?.email || !recipient?.name || !emailValidator(recipient.email).isValid
		);
		if (invalidRecipients) {
			notification.error({
				message: t('sendSignatureRequestQuickFlow.footer.invalidRecipientErrorMessage'),
			});
			return false;
		}
		return true;
	};

	const onNextButtonClick = async () => {
		if (
			signatureRequestMetadata.currentStep ===
			SignatureRequestQuickFlowDrawerSteps.ReviewAndSend
		) {
			if (!rsDocument?.name) {
				notification.error({
					message: t('SendSignatureRequest.documentNameError'),
				});
			} else if (rsDocument?.passcode && !documentSignatureSettings.passcode) {
				notification.error({
					message: t('SendSignatureRequest.securityOptions.passcodeBlankError'),
				});
			} else if (!documentSignatureSettings.expiresInDays) {
				notification.error({
					message: t('SendSignatureRequest.expirationDaysNotificationMessage'),
				});
			} else {
				updateDocumentAndRedirect();
			}
		} else if (documentTransaction.workflow === WorkflowTypes.BulkSend) {
			updateDocumentBulkSend();
		} else if (
			signatureRequestMetadata.currentStep ===
			SignatureRequestQuickFlowDrawerSteps.AddRecipients
		) {
			const recipients = documentSignatureSettings?.recipients || [];
			if (isValidRecipients(recipients)) {
				const recipientPayload = docPayloadForUpdate(recipients, rsDocument.id, true);

				await ESignatureClient.updateDocument(recipientPayload, rsDocument.id)
					.then(response => {
						if (response?.document?.workflow !== documentTransaction.workflow) {
							const payload = createDocumentTransactionPayload();
							ESignatureClient.updateDocumentTransaction(payload, documentTransaction.id);
						}
						updateSignatureRequestMetadata({
							currentStep: SignatureRequestQuickFlowDrawerSteps.ReviewAndSend,
						});
					})
					.catch(() => {
						notification.error({
							message: t('genericError'),
						});
					});
			}
		}
	};

	const handleCustomize = async () => {
		const recipients = documentSignatureSettings?.recipients || [];

		try {
			setIsCustomizeBtnDisabled(true);

			const documentTransactionPayload = createDocumentTransactionPayload();
			await ESignatureClient.updateDocumentTransaction(
				documentTransactionPayload,
				documentTransaction.id
			);

			if (
				documentTransaction.workflow !== WorkflowTypes.BulkSend &&
				recipients.length > 0
			) {
				const recipientPayload = docPayloadForUpdate(recipients, rsDocument.id, false);
				await ESignatureClient.updateDocument(recipientPayload, rsDocument.id);
			}

			setTimeout(() => {
				history.push({
					pathname: startDocumentPath.replace(':transactionId', documentTransaction?.id),
					state: {
						redirectPath: signatureRequestMetadata?.redirectPath ?? '/',
						createdFromResource: true,
						docGenRequestType: SignatureRequestTypes.Customized,
						docGenDocumentId: signatureRequestMetadata?.documentRID
							?.split('document-')
							?.pop(),
					},
				});
			}, 500);

			updateSignatureRequestMetadata({
				drawerCloseType: SignatureRequestQuickFlowDrawerCloseTypes.Customize,
			});
		} catch {
			notification.error({ message: t('genericError') });
		} finally {
			setIsCustomizeBtnDisabled(false);
		}
	};

	const shouldShowCustomiseButton = () => {
		return (
			signatureRequestMetadata?.currentStep ===
			SignatureRequestQuickFlowDrawerSteps.AddRecipients
		);
	};

	const onPreviousButtonClick = () => {
		if (
			signatureRequestMetadata.currentStep ===
			SignatureRequestQuickFlowDrawerSteps.ReviewAndSend
		) {
			updateSignatureRequestMetadata({
				currentStep: SignatureRequestQuickFlowDrawerSteps.AddRecipients,
			});
		} else if (
			signatureRequestMetadata.currentStep ===
			SignatureRequestQuickFlowDrawerSteps.AddRecipients
		) {
			updateSignatureRequestMetadata({
				drawerCloseType: SignatureRequestQuickFlowDrawerCloseTypes.PendingConfirmation,
			});
		}
	};

	return (
		<>
			{!signatureRequestMetadata?.loadingInProgress && (
				<Flex
					justify="space-between"
					align="center"
					style={{
						background: colorPalette.neutral1,
						flexDirection: 'row-reverse',
						visibility: rsDocument ? 'visible' : 'hidden',
						width: '100%',
					}}
				>
					{contextHolder}

					<div>
						{shouldShowCustomiseButton() && (
							<Button
								onClick={handleCustomize}
								data-analytics-id={SendSignatureRequestFooterAnalyticsIds.Customize}
								disabled={isCustomizeBtnDisabled}
							>
								{t('sendSignatureRequestQuickFlow.footer.customize')}
							</Button>
						)}
						<Button type="primary" onClick={onNextButtonClick}>
							{getNextButtonText(signatureRequestMetadata?.currentStep)}
						</Button>
					</div>

					<Button
						onClick={onPreviousButtonClick}
						data-analytics-id={getAnalyticsIdForPreviousButton(
							signatureRequestMetadata?.currentStep
						)}
					>
						{getPreviousButtonText(signatureRequestMetadata?.currentStep)}
					</Button>
				</Flex>
			)}
		</>
	);
};
